<template>
  <div :class="['social-networks-preview-row', { 'social-networks-preview-row--tabs': props.variant === 'tabs' }]">
    <ul :class="['social-networks-preview-row__list', { 'social-networks-preview-row__list--tabs': props.variant === 'tabs' }]">
      <li
        :class="['social-networks-preview-row__logo-wrap', { 'social-networks-preview-row__logo-wrap--tabs': props.variant === 'tabs' }]"
        :style="props.variant === 'inline' ? logoWrapStyle : undefined"
        v-for="channel in uniqueChannels"
        :key="channel"
      >
        <TooltipWrapper :disabled="props.tooltipDisabled">
          <template #hoverableElement>
            <button
              type="button"
              :title="getSocialChannelLabel(channel)"
              :aria-label="getSocialChannelLabel(channel)"
              :aria-pressed="props.selectedChannel === channel"
              :class="[
                'social-networks-preview-row__logo-button',
                {
                  'social-networks-preview-row__logo-button--selected': props.variant === 'inline' && props.selectedChannel === channel,
                  'social-networks-preview-row__logo-button--tab-active': props.variant === 'tabs' && props.selectedChannel === channel,
                  'social-networks-preview-row__logo-button--tab-inactive': props.variant === 'tabs' && props.selectedChannel !== channel,
                },
              ]"
              @click="emit('toggleSelectedChannel', channel)"
            >
              <img
                :class="[
                  'social-networks-preview-row__logo',
                  {
                    'social-networks-preview-row__logo--tab': props.variant === 'tabs',
                    'social-networks-preview-row__logo--inactive': props.isUnusedPost,
                  },
                ]"
                :src="`/images/social-logo-${channel.toLowerCase()}.svg`"
                :alt="getSocialChannelLabel(channel)"
              />
            </button>
          </template>
          <template #tooltip>{{ `${getSocialChannelLabel(channel)} preview` }}</template>
        </TooltipWrapper>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import { getSocialChannelLabel, SocialChannels, sortSocialChannelsForDisplay } from "@contracts/socialNetworks";
import type { SocialChannelData } from "@contracts/socialMediaSettings";

export type SocialPostNetworksPreviewVariant = "inline" | "tabs";

const props = withDefaults(
  defineProps<{
    socialChannels?: Array<SocialChannelData>;
    selectedChannel?: SocialChannels | null;
    logoSizePx?: number;
    isUnusedPost?: boolean;
    tooltipDisabled?: boolean;
    variant?: SocialPostNetworksPreviewVariant;
  }>(),
  {
    socialChannels: () => [],
    selectedChannel: null,
    logoSizePx: 24,
    isUnusedPost: false,
    tooltipDisabled: false,
    variant: "inline",
  }
);

const emit = defineEmits<{
  (e: "toggleSelectedChannel", channel: SocialChannels): void;
}>();

const uniqueChannels = computed<Array<SocialChannels>>(() => {
  const usedChannels = new Set<SocialChannels>();
  const deduped: Array<SocialChannels> = [];
  for (const channelItem of props.socialChannels) {
    const channelType = channelItem.channelType;
    if (usedChannels.has(channelType)) continue;
    usedChannels.add(channelType);
    deduped.push(channelType);
  }
  return sortSocialChannelsForDisplay(deduped);
});

const logoWrapStyle = computed(() => ({ width: `${props.logoSizePx}px` }));
</script>

<style scoped lang="scss">
.social-networks-preview-row {
  flex-shrink: 0;

  &__list {
    gap: 4px;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    list-style: none;

    &--tabs {
      gap: 0;
      height: 28px;
      align-items: center;
    }
  }

  &__logo-wrap {
    width: 21px;
    display: flex;
    align-items: center;

    &--tabs + &--tabs {
      margin-left: -1px;
    }

    /*&--tabs:has(.social-networks-preview-row__logo-button--tab-active) + &--tabs {
      margin-left: 4px;
    }*/

    &--tabs {
      width: auto;
      height: 28px;
      align-items: center;
    }
  }

  &__logo-button {
    border: 1px solid transparent;
    padding: 0;
    width: 100%;
    display: flex;
    cursor: pointer;
    background: transparent;
    border-radius: 8px;
    align-items: center;
    justify-content: center;
    transition:
      background-color 0.16s ease,
      border-color 0.16s ease;

    &--selected {
      padding: 1.5px;
      background: rgba(0, 0, 0, 0.14);
      border-color: rgba(0, 0, 0, 0.2);
    }

    &--tab-active {
      width: 36px;
      height: 28px;
      border: 1px solid #d7d7d7;
      border-bottom: none;
      border-radius: 8px 8px 0 0;
      background: white;
      position: relative;
      z-index: 2;
      /* box-shadow:
        -1px 0 2px rgba(0, 0, 0, 0.04),
        1px 0 2px rgba(0, 0, 0, 0.04);*/

      /* Keep icon above the decorative seam mask below */
      > * {
        position: relative;
        z-index: 1;
      }

      /* Mask side borders below the card top edge, same technique as half-pill */
      &::after {
        content: "";
        position: absolute;
        bottom: -1px;
        left: -1px;
        right: -1px;
        height: 14px;
        z-index: 0;
        pointer-events: none;
        border: 2px solid white;
        border-top: 0;
      }
    }

    &--tab-inactive {
      width: 34px;
      height: 28px;
      border: 1px solid #d0d0d0;
      border-radius: 8px;
      background: #ececec;
      position: relative;
      z-index: 3;
    }
  }

  &__logo {
    width: 100%;
    display: block;

    &--tab {
      width: 21px;
      height: 21px;
      margin: 0 1px;
      flex-shrink: 0;
    }

    &--inactive {
      opacity: 0.4;
      filter: grayscale(1);
    }
  }
}
</style>
